/**
 * Every user-facing string in one place. Single locale today (sv-SE), but keeping
 * the strings centralised means adding a second locale is additive rather than a
 * hunt through 90 components.
 */

import type {
  AlertType,
  BatchStatus,
  DateType,
  DeliveryStatus,
  DiscrepancyReason,
  DisposalMethod,
  EquipmentStatus,
  EquipmentType,
  MaintenanceCategory,
  MaintenanceStatus,
  OrderStatus,
  PaymentMethod,
  Priority,
  RestockRequestStatus,
  Role,
  Severity,
  StationStatus,
  StockCountStatus,
  StockCountType,
  StorageType,
  TransactionType,
  WasteReason,
  Allergen,
  CustomerStatus,
  SensorKind,
  SensorStatus,
  AuditAction,
} from '@/generated/prisma'

export const t = {
  brand: {
    name: 'Wellday',
    tagline: 'Gör varje dag till en Wellday',
    taglineEn: 'Make every day a Wellday',
  },

  common: {
    save: 'Spara',
    cancel: 'Avbryt',
    close: 'Stäng',
    confirm: 'Bekräfta',
    create: 'Skapa',
    edit: 'Redigera',
    delete: 'Ta bort',
    search: 'Sök',
    filter: 'Filtrera',
    clear: 'Rensa',
    back: 'Tillbaka',
    next: 'Nästa',
    previous: 'Föregående',
    export: 'Exportera',
    loading: 'Laddar…',
    noResults: 'Inga träffar',
    all: 'Alla',
    none: 'Ingen',
    yes: 'Ja',
    no: 'Nej',
    of: 'av',
    showing: 'Visar',
    total: 'Totalt',
    details: 'Detaljer',
    history: 'Historik',
    status: 'Status',
    actions: 'Åtgärder',
    quantity: 'Antal',
    product: 'Produkt',
    station: 'Station',
    location: 'Plats',
    customer: 'Kund',
    equipment: 'Utrustning',
    batch: 'Batch',
    date: 'Datum',
    note: 'Anteckning',
    reason: 'Orsak',
    required: 'Obligatoriskt',
    saved: 'Sparat',
    somethingWentWrong: 'Något gick fel. Försök igen.',
    accessDenied: 'Du har inte behörighet till den här sidan.',
    notFound: 'Sidan kunde inte hittas.',
  },

  nav: {
    overview: 'Översikt',
    customers: 'Kunder',
    locations: 'Platser',
    stations: 'Stationer',
    products: 'Produkter',
    categories: 'Kategorier',
    suppliers: 'Leverantörer',
    priceLists: 'Prislistor',
    assortments: 'Sortiment',
    inventory: 'Lager',
    batches: 'Batcher',
    ledger: 'Lagerhändelser',
    expiring: 'Utgår snart',
    deliveries: 'Leveranser',
    restock: 'Påfyllning',
    counts: 'Inventeringar',
    waste: 'Svinn',
    equipment: 'Utrustning',
    sensors: 'Sensorer',
    maintenance: 'Underhåll',
    cases: 'Ärenden',
    alerts: 'Larm',
    reports: 'Rapporter',
    users: 'Användare',
    audit: 'Revisionslogg',
    settings: 'Inställningar',
    consumption: 'Förbrukning',
    signOut: 'Logga ut',
    myStations: 'Mina stationer',
  },

  auth: {
    signIn: 'Logga in',
    signInTitle: 'Logga in på Wellday',
    signInSubtitle: 'Ange dina uppgifter för att fortsätta',
    email: 'E-postadress',
    password: 'Lösenord',
    forgotPassword: 'Glömt lösenord?',
    invalidCredentials: 'Fel e-postadress eller lösenord.',
    accountSuspended: 'Kontot är spärrat. Kontakta din administratör.',
    tooManyAttempts: 'För många försök. Vänta en stund och försök igen.',
    signedOut: 'Du är utloggad.',
  },

  inventory: {
    onHand: 'I lager',
    parLevel: 'Målnivå',
    minLevel: 'Miniminivå',
    maxLevel: 'Maxnivå',
    expiryDate: 'Utgångsdatum',
    bestBefore: 'Bäst före',
    useBy: 'Sista förbrukningsdag',
    receivedAt: 'Mottagen',
    lotCode: 'Batchnummer',
    unitCost: 'Inköpspris',
    stockValue: 'Lagervärde',
    daysOfCover: 'Täckning',
    fefoOrder: 'FEFO-ordning',
    balanceAfter: 'Saldo efter',
    outOfStock: 'Slut i lager',
    lowStock: 'Låg nivå',
    expiringSoon: 'Utgår snart',
    expired: 'Utgången',
    ok: 'OK',
    recordWaste: 'Registrera svinn',
    requestRestock: 'Begär påfyllning',
    startCount: 'Starta inventering',
  },

  workflow: {
    receiveDelivery: 'Ta emot leverans',
    confirmPlacement: 'Bekräfta placering',
    submitCount: 'Skicka in inventering',
    approveCount: 'Godkänn inventering',
    rejectCount: 'Avvisa inventering',
    reportIssue: 'Rapportera fel',
    createCase: 'Skapa ärende',
    acknowledge: 'Kvittera',
    resolve: 'Åtgärda',
    assign: 'Tilldela',
  },

  consumer: {
    title: 'Vad finns i stationen?',
    addToCart: 'Lägg i kundvagn',
    cart: 'Kundvagn',
    checkout: 'Till kassan',
    receipt: 'Kvitto',
    emptyCart: 'Din kundvagn är tom.',
    allergens: 'Allergener',
    nutrition: 'Näringsvärde',
    ingredients: 'Ingredienser',
    perHundred: 'Per 100 g/ml',
    pay: 'Betala',
    payWithCompanyAccount: 'Betala med företagskonto',
    soldOut: 'Slutsåld',
    notPurchasable: 'Kan ej köpas — passerat sista förbrukningsdag',
    thankYou: 'Tack för ditt köp!',
    scanToBuy: 'Skanna för att handla',
  },

  reports: {
    consumption: 'Förbrukning',
    waste: 'Svinn',
    wasteRate: 'Svinnandel',
    deliveries: 'Leveransprecision',
    temperature: 'Temperaturefterlevnad',
    stockValue: 'Lagervärde',
    sales: 'Försäljning',
    fillRate: 'Fyllnadsgrad',
    topProducts: 'Mest sålda produkter',
    period: 'Period',
    last7: 'Senaste 7 dagarna',
    last30: 'Senaste 30 dagarna',
    last90: 'Senaste 90 dagarna',
  },
} as const

// ── Enum labels ─────────────────────────────────────────────────────────────

export const roleLabel: Record<Role, string> = {
  WELLDAY_ADMIN: 'Wellday-administratör',
  WELLDAY_OPERATIONS: 'Wellday-drift',
  CUSTOMER_ADMIN: 'Kundadministratör',
  STATION_ADMIN: 'Stationsansvarig',
  CONSUMER: 'Konsument',
}

export const customerStatusLabel: Record<CustomerStatus, string> = {
  PROSPECT: 'Prospekt',
  ACTIVE: 'Aktiv',
  PAUSED: 'Pausad',
  CHURNED: 'Avslutad',
}

export const stationStatusLabel: Record<StationStatus, string> = {
  PLANNED: 'Planerad',
  ACTIVE: 'Aktiv',
  PAUSED: 'Pausad',
  DECOMMISSIONED: 'Avvecklad',
}

export const storageTypeLabel: Record<StorageType, string> = {
  AMBIENT: 'Rumstempererat',
  CHILLED: 'Kylt',
  FROZEN: 'Fryst',
}

export const dateTypeLabel: Record<DateType, string> = {
  BEST_BEFORE: 'Bäst före',
  USE_BY: 'Sista förbrukningsdag',
}

export const batchStatusLabel: Record<BatchStatus, string> = {
  AVAILABLE: 'Tillgänglig',
  QUARANTINED: 'Karantän',
  EXPIRED: 'Utgången',
  WRITTEN_OFF: 'Avskriven',
  DEPLETED: 'Slut',
}

export const transactionTypeLabel: Record<TransactionType, string> = {
  DELIVERY_RECEIPT: 'Leveransmottagning',
  RESTOCK_PLACEMENT: 'Påfyllning i utrustning',
  SALE: 'Försäljning',
  CONSUMPTION: 'Uttag',
  WASTE_EXPIRED: 'Svinn – utgången',
  WASTE_DAMAGED: 'Svinn – skadad',
  WASTE_TEMPERATURE: 'Svinn – temperaturavvikelse',
  WASTE_OTHER: 'Svinn – övrigt',
  COUNT_ADJUSTMENT: 'Inventeringsjustering',
  TRANSFER_IN: 'Överföring in',
  TRANSFER_OUT: 'Överföring ut',
  RETURN_TO_WELLDAY: 'Retur till Wellday',
  CORRECTION: 'Korrigering',
}

export const equipmentTypeLabel: Record<EquipmentType, string> = {
  FRIDGE: 'Kylskåp',
  FREEZER: 'Frys',
  AMBIENT_SHELF: 'Hylla',
  COFFEE_MACHINE: 'Kaffemaskin',
  MICROWAVE: 'Mikrovågsugn',
  PAYMENT_TERMINAL: 'Betalterminal',
  SMART_LOCK: 'Smartlås',
}

export const equipmentStatusLabel: Record<EquipmentStatus, string> = {
  OPERATIONAL: 'I drift',
  DEGRADED: 'Nedsatt funktion',
  OUT_OF_SERVICE: 'Ur drift',
  MAINTENANCE: 'Under service',
  DECOMMISSIONED: 'Avvecklad',
}

export const sensorKindLabel: Record<SensorKind, string> = {
  TEMPERATURE: 'Temperatur',
  HUMIDITY: 'Luftfuktighet',
  DOOR: 'Dörr',
  POWER: 'Ström',
}

export const sensorStatusLabel: Record<SensorStatus, string> = {
  ACTIVE: 'Aktiv',
  OFFLINE: 'Offline',
  FAULTY: 'Felaktig',
  RETIRED: 'Avvecklad',
}

export const deliveryStatusLabel: Record<DeliveryStatus, string> = {
  PLANNED: 'Planerad',
  PICKED: 'Plockad',
  IN_TRANSIT: 'Under transport',
  DELIVERED: 'Levererad',
  PARTIALLY_DELIVERED: 'Delvis levererad',
  CANCELLED: 'Inställd',
}

export const restockStatusLabel: Record<RestockRequestStatus, string> = {
  NEW: 'Ny',
  APPROVED: 'Godkänd',
  REJECTED: 'Avvisad',
  PLANNED: 'Planerad',
  FULFILLED: 'Levererad',
  CANCELLED: 'Avbruten',
}

export const priorityLabel: Record<Priority, string> = {
  LOW: 'Låg',
  NORMAL: 'Normal',
  HIGH: 'Hög',
  URGENT: 'Akut',
}

export const countTypeLabel: Record<StockCountType, string> = {
  FULL: 'Fullständig',
  CYCLE: 'Cyklisk',
  SPOT: 'Stickprov',
}

export const countStatusLabel: Record<StockCountStatus, string> = {
  DRAFT: 'Utkast',
  IN_PROGRESS: 'Pågår',
  SUBMITTED: 'Inskickad',
  APPROVED: 'Godkänd',
  REJECTED: 'Avvisad',
}

export const discrepancyReasonLabel: Record<DiscrepancyReason, string> = {
  STOLD: 'Stöld',
  SVINN_EJ_REGISTRERAT: 'Oregistrerat svinn',
  FELREGISTRERING: 'Felregistrering',
  LEVERANSDIFFERENS: 'Leveransdifferens',
  FLYTTAD_TILL_ANNAN_STATION: 'Flyttad till annan station',
  OKAND: 'Okänd',
}

export const wasteReasonLabel: Record<WasteReason, string> = {
  EXPIRED: 'Utgånget datum',
  DAMAGED: 'Skadad',
  TEMPERATURE_DEVIATION: 'Temperaturavvikelse',
  RECALL: 'Återkallelse',
  CUSTOMER_RETURN: 'Kundretur',
  OTHER: 'Övrigt',
}

export const disposalMethodLabel: Record<DisposalMethod, string> = {
  DISCARDED: 'Kasserad',
  DONATED: 'Skänkt',
  RETURNED_TO_WELLDAY: 'Retur till Wellday',
  COMPOSTED: 'Komposterad',
}

export const maintenanceCategoryLabel: Record<MaintenanceCategory, string> = {
  TEMPERATURE: 'Temperatur',
  MECHANICAL: 'Mekaniskt',
  ELECTRICAL: 'Elektriskt',
  CLEANING: 'Rengöring',
  PAYMENT: 'Betalning',
  CONNECTIVITY: 'Uppkoppling',
  OTHER: 'Övrigt',
}

export const maintenanceStatusLabel: Record<MaintenanceStatus, string> = {
  REPORTED: 'Anmäld',
  TRIAGED: 'Bedömd',
  ASSIGNED: 'Tilldelad',
  IN_PROGRESS: 'Pågår',
  WAITING_PARTS: 'Väntar på delar',
  RESOLVED: 'Åtgärdad',
  CLOSED: 'Avslutad',
}

export const severityLabel: Record<Severity, string> = {
  LOW: 'Låg',
  MEDIUM: 'Medel',
  HIGH: 'Hög',
  CRITICAL: 'Kritisk',
}

export const alertTypeLabel: Record<AlertType, string> = {
  EXPIRING_SOON: 'Utgår snart',
  EXPIRED: 'Utgången vara',
  LOW_STOCK: 'Låg lagernivå',
  OUT_OF_STOCK: 'Slut i lager',
  TEMPERATURE_EXCURSION: 'Temperaturavvikelse',
  EQUIPMENT_OFFLINE: 'Utrustning offline',
  COUNT_DISCREPANCY: 'Inventeringsdifferens',
  DELIVERY_DELAYED: 'Försenad leverans',
  SLA_BREACH: 'SLA överskriden',
}

export const orderStatusLabel: Record<OrderStatus, string> = {
  CART: 'Kundvagn',
  PENDING_PAYMENT: 'Väntar på betalning',
  PAID: 'Betald',
  FAILED: 'Misslyckad',
  CANCELLED: 'Avbruten',
  REFUNDED: 'Återbetald',
}

export const paymentMethodLabel: Record<PaymentMethod, string> = {
  EMPLOYEE_ACCOUNT: 'Företagskonto',
  SWISH_MOCK: 'Swish',
  CARD_MOCK: 'Kort',
}

export const allergenLabel: Record<Allergen, string> = {
  GLUTEN: 'Gluten',
  LAKTOS: 'Laktos',
  MJOLK: 'Mjölk',
  AGG: 'Ägg',
  NOTTER: 'Nötter',
  JORDNOTTER: 'Jordnötter',
  SOJA: 'Soja',
  FISK: 'Fisk',
  SKALDJUR: 'Skaldjur',
  SELLERI: 'Selleri',
  SENAP: 'Senap',
  SESAM: 'Sesam',
  SULFIT: 'Sulfit',
  LUPIN: 'Lupin',
  BLOTDJUR: 'Blötdjur',
}

export const auditActionLabel: Record<AuditAction, string> = {
  CREATE: 'Skapade',
  UPDATE: 'Uppdaterade',
  DELETE: 'Tog bort',
  APPROVE: 'Godkände',
  REJECT: 'Avvisade',
  LOGIN: 'Loggade in',
  LOGIN_FAILED: 'Misslyckad inloggning',
  LOGOUT: 'Loggade ut',
  EXPORT: 'Exporterade',
  ACKNOWLEDGE: 'Kvitterade',
  RESOLVE: 'Åtgärdade',
  ASSIGN: 'Tilldelade',
  RECEIVE: 'Tog emot',
  ADJUST: 'Justerade',
}
